import { Hidden, Tabs } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { InfoBarWrapper } from '../../../../../global/components/layout/info-bar-wrapper';
import { genericStepperStyles } from '../../../../../global/constants/theme/styles/components/generic-stepper';
import { isDioAdmin, isInstitutionOfficer } from '../../../../../global/helpers/auth-helpers';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { ArcRoutes } from '../../../constants/routes';
import { NewHireProps } from '../../../models/arc-epi-props';
import { Employee } from '../../employment/employment-model';
import { NewHireEnrollmentStep } from '../../new-hire-enrollment/models/new-hire-enrollment-models';
import { NewHireEnrollmentRoutes } from '../../new-hire-enrollment/models/new-hire-routes';
import { setCurrentNewHireEnrollmentStep } from '../../new-hire-enrollment/slice/new-hire-enrollment-slices';
import { NameClientNumber } from '../../../../../global/components/name-client-number';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { useQuery } from '../../../../../global/hooks/useQuery';


interface Props {
	basePath: string;
	hideSteps: boolean;
	hideAll?: boolean;
	titleOverwrite?: string;
}

export function NewHireStepper(props: Props) {

	const { hideSteps, basePath, hideAll, titleOverwrite } = props;

	const classes = genericStepperStyles();
	const dispatch = useDispatch();
	const currentStep = useSelector<TcsmStates, NewHireEnrollmentStep>(state => state?.newHireEnrollmentState?.currentStep);
	const settings = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
	const history = useHistory();
	const currentEmployee = useSelector<TcsmStates, Employee>(state => state?.employmentState?.currentEmployee);
	const employmentRecord = `${basePath}${NewHireEnrollmentRoutes.EmployeeRecord}${!currentEmployee ? ArcRoutes?.StipNewHire : AccountInfoRoutes.PersonalInfoSegment}`;
	const whoWillEnroll = `${basePath}${NewHireEnrollmentRoutes.WhoWillEnroll}`;
	const benefitEnrollment = `${basePath}${NewHireEnrollmentRoutes.BenefitEnrollment}${AccountInfoRoutes.PersonalInfoSegment}`;
	const isRoleAllowed = isDioAdmin() || isInstitutionOfficer();

	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);

	const onClick = (step: NewHireEnrollmentStep, url: string) => {
		dispatch(setCurrentNewHireEnrollmentStep(step));
		history.push(url);
	};

	const formType = useQuery()?.get('success');
	const currentPath = window?.location?.pathname?.toLowerCase();
	const isNonStip = currentPath?.includes(`${NewHireEnrollmentRoutes.EmployeeRecord}${ArcRoutes.NonStipNewHire}`) || formType === FormTypes.NON_STIP_NEW_HIRE;
	const benefitEnrollAllowed = !isNonStip && isRoleAllowed;

	let stepIndex = 0;

	switch (currentStep) {
		case NewHireEnrollmentStep.WHO_WILL_ENROLL:
			stepIndex = 1;
			break;
		case NewHireEnrollmentStep.BENEFIT_ENROLLMENT:
			stepIndex = 2;
			break;

		case NewHireEnrollmentStep.EMPLOYEE_RECORD:
		default:
			stepIndex = 0;
			break;
	}

	if (hideAll) {
		return null;
	}

	return (
		<div className={`${classes.genericStepperContainer} generic-stepper-tabs`}>
			<Hidden mdDown>
				<div className='generic-stepper-tab-desktop'>
					{!hideSteps &&
						<div className='generic-stepper-tab-desktop-inner'>

							<button
								className='generic-stepper-tab-item'
								type='button'
								onClick={() => onClick(NewHireEnrollmentStep.EMPLOYEE_RECORD, employmentRecord)}
								disabled={currentStep === NewHireEnrollmentStep.EMPLOYEE_RECORD}
							>
								<span className={`${currentStep === NewHireEnrollmentStep.EMPLOYEE_RECORD ? 'h4-styled' : 'h4-alt'}`}>{settings?.employmentRecordStepName}</span>
							</button>

							{
								benefitEnrollAllowed &&
								<button
									className='generic-stepper-tab-item'
									type='button'
									onClick={() => onClick(NewHireEnrollmentStep.WHO_WILL_ENROLL, whoWillEnroll)}
									disabled={currentStep === NewHireEnrollmentStep.WHO_WILL_ENROLL || (stepIndex < 1)}>
									<span className={`${currentStep === NewHireEnrollmentStep.WHO_WILL_ENROLL ? 'h4-styled' : 'h4-alt'}`}>{settings?.whoWillEnrollStepName}</span>
								</button>
							}

							{
								benefitEnrollAllowed &&
								<button
									className='generic-stepper-tab-item'
									type='button'
									onClick={() => onClick(NewHireEnrollmentStep.BENEFIT_ENROLLMENT, benefitEnrollment)}
									disabled={currentStep === NewHireEnrollmentStep.BENEFIT_ENROLLMENT || (stepIndex < 2)}
								>
									<span className={`${currentStep === NewHireEnrollmentStep.BENEFIT_ENROLLMENT ? 'h4-styled' : 'h4-alt'}`}>{settings?.benefitEnrollmentStepName}</span>
								</button>
							}

						</div>}

					<div className='generic-stepper-infobar'>
						<h4> {titleOverwrite || settings?.enrollmentTitle}  </h4>
						<NameClientNumber client={currentEmployee} clientNumberLabel={labelSettings?.clientNumberLabel} />
					</div>
				</div>
			</Hidden >
			{/*Below is the Mobile Stepper with mobile infobar*/}
			< Hidden mdUp >
				<InfoBarWrapper>
					<div>
						<h3> {titleOverwrite || settings?.enrollmentTitle} </h3>
						<NameClientNumber client={currentEmployee} clientNumberLabel={labelSettings?.clientNumberLabel} />
					</div>
				</InfoBarWrapper>
				{
					!hideSteps &&
					<Tabs
						value={stepIndex > -1 ? stepIndex : 0}
						variant='scrollable'
						scrollButtons>

						<button
							className='generic-stepper-tab-item'
							type='button'
							onClick={() => onClick(NewHireEnrollmentStep.EMPLOYEE_RECORD, employmentRecord)}
							disabled={currentStep === NewHireEnrollmentStep.EMPLOYEE_RECORD}
						>
							<span className={`${currentStep === NewHireEnrollmentStep.EMPLOYEE_RECORD ? 'h4-styled' : 'h4-alt'}`}>{settings?.employmentRecordStepName}</span>
						</button>
						{
							benefitEnrollAllowed &&
							<button
								className='generic-stepper-tab-item'
								type='button'
								onClick={() => onClick(NewHireEnrollmentStep.WHO_WILL_ENROLL, whoWillEnroll)}
								disabled={currentStep === NewHireEnrollmentStep.WHO_WILL_ENROLL || (stepIndex < 1)}
							>
								<span className={`${currentStep === NewHireEnrollmentStep.WHO_WILL_ENROLL ? 'h4-styled' : 'h4-alt'}`}>{settings?.whoWillEnrollStepName}</span>
							</button>
						}

						{
							benefitEnrollAllowed &&
							<button
								className='generic-stepper-tab-item'
								type='button'
								onClick={() => onClick(NewHireEnrollmentStep.BENEFIT_ENROLLMENT, benefitEnrollment)}
								disabled={currentStep === NewHireEnrollmentStep.BENEFIT_ENROLLMENT || (stepIndex < 2)}
							>
								<span className={`${currentStep === NewHireEnrollmentStep.BENEFIT_ENROLLMENT ? 'h4-styled' : 'h4-alt'}`}>{settings?.benefitEnrollmentStepName}</span>
							</button>
						}
					</Tabs>
				}
			</Hidden >
		</div >
	);

}

NewHireStepper.defaultProps = {
	hideAll: false,
	titleOverwrite: null,
};